/**
 * Home — what needs a person right now, and the numbers that say whether the day is going well.
 *
 * Two requests, and each one is a single source of truth for what it answers.
 *
 * `GET /api/v1/home` returns unanswered conversations, unclaimed handoffs and overdue promises
 * computed together, so the three lists cannot disagree about what time it is.
 *
 * `GET /api/v1/analytics?panels=home` returns the tiles — which numbers Home shows is declared on
 * the SERVER (`src/metrics/panels.ts`), not listed here. That matters for one tile in particular:
 * the badge over "Open handoffs" counts UNCLAIMED handoffs, because a badge counting claimed work
 * above a list showing unclaimed work is how a console ends up saying 5 over a list of 3. Keeping
 * the list of tiles in this file is how the two drift.
 *
 * Every tile carries its definition, its window and its tense, and a tile that cannot compute
 * prints the sentence saying why instead of a zero (C7).
 *
 * Author: Danish Khan
 */
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import type { ReactNode } from 'react';
import { Link, useNavigate } from 'react-router';
import { api } from '../lib/api.ts';
import { formatAgo, formatDuration, humanize } from '../lib/format.ts';
import { useUser } from '../lib/session.tsx';
import { phoneFor } from '../lib/pii.ts';
import { MetricCard } from './Analytics.tsx';
import { analyticsApi, type AnalyticsClient } from './analyticsApi.ts';
import { Pill, TierPill } from '../components/Pill.tsx';
import { EmptyState } from '../components/EmptyState.tsx';
import { ErrorState } from '../components/ErrorState.tsx';

export interface HomeProps {
  /** Injected in tests. The tiles are otherwise read from `/api/v1/analytics?panels=home`. */
  analytics?: AnalyticsClient;
}

export function HomePage({ analytics = analyticsApi }: HomeProps = {}): ReactNode {
  const user = useUser();
  const navigate = useNavigate();

  const metrics = useQuery({ queryKey: ['analytics', 'home'], queryFn: () => analytics.panels(['home']) });
  const home = useQuery({ queryKey: ['home'], queryFn: () => api.home() });

  if (metrics.isError) return <ErrorState error={metrics.error} context="load the dashboard" onRetry={() => void metrics.refetch()} />;

  const tiles = metrics.data?.panels[0]?.metrics ?? [];
  const lowerIsBetter = new Set(metrics.data?.lowerIsBetter ?? []);

  const unanswered = home.data?.unanswered ?? [];
  const handoffs = home.data?.unclaimedHandoffs ?? [];
  const promises = home.data?.overduePromises ?? [];

  return (
    <div className="h1-scroll h-full overflow-y-auto px-5 py-5">
      <div className="mx-auto flex max-w-6xl flex-col gap-6">
        <section>
          <h2 className="mb-2.5 text-sm font-semibold text-fg">
            Today · <span className="font-normal text-fg-muted">every number opens to show the query behind it</span>
          </h2>
          <div className="flex flex-wrap gap-2.5">
            {metrics.isLoading
              ? Array.from({ length: 7 }, (_, i) => (
                  <div key={i} className="h1-card h-[86px] min-w-[13rem] flex-1 animate-pulse bg-surface-2" />
                ))
              : tiles.length === 0
                ? (
                  // The server decides which tiles Home shows. If it answers with no `home` panel
                  // the heading above used to stand over nothing at all, which reads as seven
                  // numbers that are all zero rather than as a panel that did not arrive.
                  <p className="text-sm text-fg-muted">
                    No tiles came back for this panel. Nothing is claimed about today’s numbers until they do.
                  </p>
                )
                : tiles.map((m) => (
                    <MetricCard key={m.name} metric={m} lowerIsBetter={lowerIsBetter.has(m.name)} />
                  ))}
          </div>
        </section>

        <div className="grid gap-5 lg:grid-cols-2">
          <section className="h1-card flex min-h-64 flex-col">
            <header className="flex items-center justify-between border-b border-line px-4 py-2.5">
              <h2 className="text-sm font-semibold text-fg">Waiting on us</h2>
              <Link to="/inbox?view=unanswered" className="text-xs font-medium text-brand-ink hover:underline">
                Open inbox
              </Link>
            </header>
            {home.isError ? (
              <ErrorState error={home.error} context="load unanswered conversations" onRetry={() => void home.refetch()} />
            ) : home.isLoading ? (
              <p className="px-4 py-6 text-sm text-fg-muted">Loading…</p>
            ) : unanswered.length === 0 ? (
              <EmptyState title="Nothing is waiting" body="Every inbound message has an answer after it." />
            ) : (
              <ul className="divide-y divide-[var(--h1-border)]">
                {unanswered.slice(0, 6).map((c) => {
                  const phone = phoneFor(user.role, { phone: c.phone, contactHash: c.contactHash });
                  return (
                    <li key={c.conversationId}>
                      <button
                        type="button"
                        onClick={() => navigate(`/inbox/${c.conversationId}`)}
                        className="flex w-full items-center gap-3 px-4 py-2.5 text-left hover:bg-surface-2"
                      >
                        <span className="min-w-0 flex-1">
                          <span className="flex items-center gap-2">
                            <span className="truncate text-sm font-medium text-fg">{c.displayName}</span>
                            <TierPill tier={c.tier} />
                          </span>
                          <span className="mt-0.5 block truncate text-xs text-fg-muted">{c.lastMessagePreview}</span>
                          <span className={phone.redacted ? 'text-[10px] font-mono text-fg-subtle' : 'text-[10px] text-fg-subtle'}>
                            {phone.text}
                            {phone.redacted && ' · number hidden for your role'}
                          </span>
                        </span>
                        <span
                          className={
                            c.unansweredMs > 30 * 60_000
                              ? 'shrink-0 text-xs font-semibold text-danger'
                              : 'shrink-0 text-xs text-fg-muted'
                          }
                        >
                          {formatDuration(c.unansweredMs)}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          <section className="h1-card flex min-h-64 flex-col">
            <header className="flex items-center justify-between border-b border-line px-4 py-2.5">
              <h2 className="text-sm font-semibold text-fg">Open handoffs</h2>
              <Link to="/handoffs" className="text-xs font-medium text-brand-ink hover:underline">
                Open queue
              </Link>
            </header>
            {home.isError ? (
              <ErrorState error={home.error} context="load handoffs" onRetry={() => void home.refetch()} />
            ) : home.isLoading ? (
              <p className="px-4 py-6 text-sm text-fg-muted">Loading…</p>
            ) : handoffs.length === 0 ? (
              <EmptyState title="No unclaimed handoffs" body="Every customer who asked for a person has one." />
            ) : (
              <ul className="divide-y divide-[var(--h1-border)]">
                {handoffs.map((h) => (
                  <li key={h.id}>
                    <button
                      type="button"
                      onClick={() => navigate('/handoffs')}
                      className="flex w-full items-start gap-3 px-4 py-2.5 text-left hover:bg-surface-2"
                    >
                      <span className="min-w-0 flex-1">
                        <span className="flex flex-wrap items-center gap-2">
                          <span className="truncate text-sm font-medium text-fg">{h.displayName}</span>
                          <span className="font-mono text-[11px] text-fg-subtle">{h.code}</span>
                          <Pill tone={h.basis === 'customer_consent' ? 'ok' : h.basis === 'policy_required' ? 'warn' : 'neutral'}>
                            {h.basis.replace('_', ' ')}
                          </Pill>
                        </span>
                        {h.quote && <span className="mt-0.5 block truncate text-xs italic text-fg-muted">“{h.quote}”</span>}
                      </span>
                      <span className="shrink-0 text-xs text-fg-muted">{formatAgo(h.openedAt)}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>

        {/*
          The third queue, and the one nobody has a screen for anywhere else: something was
          promised to a customer, the time passed, and no human has done it. It is deliberately
          not hidden when empty — an empty list here is a real and good state, and a section that
          vanishes teaches people it does not exist.
        */}
        <section className="h1-card flex flex-col">
          <header className="flex items-center justify-between border-b border-line px-4 py-2.5">
            <h2 className="text-sm font-semibold text-fg">Promises past their due time</h2>
            <span className="text-[11px] text-fg-subtle">a person owes every one of these</span>
          </header>
          {home.isError ? (
            <ErrorState error={home.error} context="load overdue promises" onRetry={() => void home.refetch()} />
          ) : home.isLoading ? (
            <p className="px-4 py-6 text-sm text-fg-muted">Loading…</p>
          ) : promises.length === 0 ? (
            <EmptyState title="Nothing is overdue" body="Every promise made to a customer is still inside its due time." />
          ) : (
            <ul className="divide-y divide-[var(--h1-border)]">
              {promises.map((p) => (
                <li key={p.id}>
                  <button
                    type="button"
                    onClick={() => navigate(`/inbox/${p.conversationId}`)}
                    className="flex w-full items-center gap-3 px-4 py-2.5 text-left hover:bg-surface-2"
                  >
                    <span className="min-w-0 flex-1">
                      <span className="flex flex-wrap items-center gap-2">
                        <span className="truncate text-sm font-medium text-fg">{p.displayName}</span>
                        {/* `crm.promises.kind` is passed through verbatim by `routes/home.ts`. It
                            is a column value — `send_quote`, `call_back` — not a label. */}
                        <Pill tone="neutral">{humanize(p.kind)}</Pill>
                      </span>
                      <span className="mt-0.5 block truncate text-xs text-fg-muted">{p.text}</span>
                    </span>
                    <span className={clsx('shrink-0 text-xs font-semibold', p.overdue ? 'text-danger' : 'text-fg-muted')}>
                      due {formatAgo(p.dueAt)}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}
